import React, { useCallback, useEffect, useState } from 'react';
import { MedicineBoxOutlined, ReloadOutlined, LoadingOutlined, CloseOutlined } from '@ant-design/icons';
import { ehealthAPI } from '@/api/endpoints/ehealth.js';
import styles from '@/styles/pages/ehealth/EhealthPage.module.css';

const REFRESH_INTERVAL_MS = 10000;

const formatDate = (s) => (s ? new Date(s).toLocaleString('ru-RU') : '—');
// "2026-10-28T13:00" -> "28.10.2026 13:00"
const formatLocal = (s) => {
  const [d, t] = s.split('T');
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y} ${t}`;
};

const EhealthPage = () => {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');
  const [winFrom, setWinFrom] = useState('');
  const [winTo, setWinTo] = useState('');
  const [doctors, setDoctors] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [token, setToken] = useState('');
  const [cookies, setCookies] = useState('');
  const [realAddress, setRealAddress] = useState('');
  const [interval, setIntervalSec] = useState(300);
  const [hospitalId, setHospitalId] = useState(14);
  const [specId, setSpecId] = useState(18);
  const [patientId, setPatientId] = useState(60459);
  const [formReady, setFormReady] = useState(false);

  useEffect(() => {
    document.title = 'Талоны | DevSpace';
  }, []);

  const load = useCallback(async () => {
    const res = await ehealthAPI.get();
    if (res.success) {
      setCfg(res.data);
      setError(null);
      setFormReady((ready) => {
        if (!ready) {
          const d = res.data;
          setLogin(d.login || '');
          setRealAddress(d.real_address || '');
          setIntervalSec(d.interval_seconds);
          setHospitalId(d.hospital_id);
          setSpecId(d.spec_id);
          setPatientId(d.patient_id);
          setDoctors((d.doctor_names || []).join(', '));
        }
        return true;
      });
    } else {
      setError(res.error || 'Не удалось загрузить настройки');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const save = async (payload, label = 'save') => {
    setBusy(label);
    const res = await ehealthAPI.update(payload, true);
    if (res.success) setCfg(res.data);
    setBusy('');
    return res.success;
  };

  const addWindow = async () => {
    if (!winFrom || !winTo) return;
    if (winFrom > winTo) {
      setError('Начало окна позже конца');
      return;
    }
    setError(null);
    const windows = [...cfg.windows, { from: winFrom, to: winTo }];
    if (await save({ windows }, 'windows')) {
      setWinFrom('');
      setWinTo('');
    }
  };

  const removeWindow = (i) => save({ windows: cfg.windows.filter((_, idx) => idx !== i) }, 'windows');

  const saveDoctors = () => {
    const names = doctors.split(/[,;\n]+/).map((n) => n.trim()).filter(Boolean);
    return save({ doctor_names: names }, 'doctors');
  };

  const saveCredentials = async () => {
    const payload = {};
    if (login.trim() !== (cfg.login || '')) payload.login = login.trim();
    if (password) payload.password = password;
    if (refreshToken.trim()) payload.refresh_token = refreshToken.trim();
    if (token.trim()) payload.auth_token = token.trim();
    if (cookies.trim()) payload.cookies = cookies.trim();
    if (!Object.keys(payload).length) return;
    if (await save(payload, 'creds')) {
      setPassword('');
      setRefreshToken('');
      setToken('');
      setCookies('');
    }
  };

  const saveParams = () =>
    save({
      real_address: realAddress.trim(),
      interval_seconds: Number(interval),
      hospital_id: Number(hospitalId),
      spec_id: Number(specId),
      patient_id: Number(patientId),
    }, 'params');

  const checkNow = async () => {
    setBusy('check');
    await ehealthAPI.checkNow();
    await load();
    setBusy('');
  };

  const relogin = async () => {
    setBusy('relogin');
    await ehealthAPI.relogin(true);
    await load();
    setBusy('');
  };

  const toggleAutoBook = () => {
    if (!cfg.auto_book && !window.confirm('Включить авто-запись? При появлении подходящего талона сайт запишет вас сам.')) return;
    return save({ auto_book: !cfg.auto_book }, 'autobook');
  };

  if (!cfg) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          {error ? <div className={styles.errorMessage}>{error}</div> : <div className={styles.empty}><LoadingOutlined /> Загрузка...</div>}
        </div>
      </div>
    );
  }

  const expired = cfg.token_exp && new Date(cfg.token_exp) < new Date();
  const canRelogin = cfg.refresh_token_set || (cfg.login && cfg.password_set);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}><MedicineBoxOutlined /> Мониторинг талонов</h1>
          <div className={styles.actions}>
            <button className={styles.button} onClick={checkNow} disabled={busy === 'check'}>
              {busy === 'check' ? <LoadingOutlined /> : <ReloadOutlined />} Проверить сейчас
            </button>
            <button
              className={`${styles.button} ${cfg.enabled ? styles.danger : styles.success}`}
              onClick={() => save({ enabled: !cfg.enabled }, 'toggle')}
              disabled={busy === 'toggle'}
            >
              {cfg.enabled ? 'Остановить' : 'Запустить'}
            </button>
          </div>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <section className={styles.card}>
          <h2>Статус</h2>
          <dl className={styles.kv}>
            <dt>Мониторинг</dt>
            <dd>{cfg.enabled ? `включён, каждые ${cfg.interval_seconds} с` : 'выключен'}</dd>
            <dt>Авто-запись</dt>
            <dd className={cfg.auto_book ? styles.bad : ''}>{cfg.auto_book ? 'ВКЛЮЧЕНА' : 'выключена'}</dd>
            <dt>Последняя проверка</dt>
            <dd>{formatDate(cfg.last_check_at)}</dd>
            <dt>Результат</dt>
            <dd>{cfg.last_status || '—'}</dd>
            <dt>Токен</dt>
            <dd className={expired ? styles.bad : ''}>
              {!cfg.token_set ? 'не задан' : cfg.token_exp ? `до ${formatDate(cfg.token_exp)}${expired ? ' (истёк)' : ''}` : 'задан'}
              {' '}{canRelogin ? '· обновляется автоматически' : '· нет данных для автообновления'}
            </dd>
          </dl>
        </section>

        <section className={styles.card}>
          <h2>Окна времени для поиска</h2>
          <p className={styles.muted}>Талон подходит, если его время попадает в любое из окон. Окон может быть несколько.</p>
          <div className={styles.chips}>
            {cfg.windows.length === 0 && <span className={styles.muted}>Нет окон — ничего не ищется</span>}
            {cfg.windows.map((w, i) => (
              <span key={`${w.from}-${w.to}`} className={styles.chip}>
                {formatLocal(w.from)} — {formatLocal(w.to)}
                <button onClick={() => removeWindow(i)} aria-label="Убрать"><CloseOutlined /></button>
              </span>
            ))}
          </div>
          <div className={styles.row}>
            <div><label>От</label><input type="datetime-local" value={winFrom} onChange={(e) => setWinFrom(e.target.value)} /></div>
            <div><label>До</label><input type="datetime-local" value={winTo} onChange={(e) => setWinTo(e.target.value)} /></div>
            <button className={styles.button} onClick={addWindow} disabled={!winFrom || !winTo || busy === 'windows'}>Добавить окно</button>
          </div>

          <label>Врачи — фамилия или имя через запятую (пусто — любой врач)</label>
          <div className={styles.row}>
            <input value={doctors} onChange={(e) => setDoctors(e.target.value)} placeholder="Иванова, Петров" />
            <button className={styles.button} onClick={saveDoctors} disabled={busy === 'doctors'}>Сохранить</button>
          </div>

          <h3>Найдено сейчас</h3>
          {cfg.last_found.length === 0 ? (
            <span className={styles.muted}>Подходящих талонов нет</span>
          ) : (
            <ul className={styles.found}>
              {cfg.last_found.map((f) => (
                <li key={f.id}>{f.date} {f.time} — {f.doctor}, {f.cabinet}</li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.card}>
          <h2>Авто-запись</h2>
          <p className={styles.muted}>
            Когда найден подходящий талон, сайт проверит и запишет на самый ранний из них, пришлёт результат в
            Telegram и сам выключит авто-запись и мониторинг, чтобы не записать повторно. Номер пациента: {cfg.patient_id}.
          </p>
          <button className={`${styles.button} ${cfg.auto_book ? styles.danger : styles.success}`} onClick={toggleAutoBook} disabled={busy === 'autobook'}>
            {cfg.auto_book ? 'Выключить авто-запись' : 'Включить авто-запись'}
          </button>

          {cfg.booked.length > 0 && (
            <>
              <h3>Записи, сделанные сайтом</h3>
              <ul className={styles.found}>
                {cfg.booked.map((b) => (
                  <li key={`${b.id}-${b.booked_at}`}>{b.date} {b.time} — {b.doctor}, {b.cabinet} (оформлено {formatDate(b.booked_at)})</li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className={styles.card}>
          <h2>Вход на ehealth</h2>
          <p className={styles.muted}>
            Сайт сам получает и обновляет токен: сначала по токену обновления, затем по логину и паролю. Сохранённые
            значения не показываются; пустое поле — не менять. Токен доступа и куки вручную нужны только как запасной вариант.
          </p>
          <div className={styles.grid}>
            <div><label>Логин</label><input value={login} onChange={(e) => setLogin(e.target.value)} autoComplete="off" /></div>
            <div>
              <label>Пароль {cfg.password_set && '(сохранён)'}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </div>
          </div>
          <label>Токен обновления {cfg.refresh_token_set && '(сохранён)'}</label>
          <textarea rows={2} value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} />
          <label>Токен доступа (Bearer) — запасной вариант</label>
          <textarea rows={2} value={token} onChange={(e) => setToken(e.target.value)} placeholder="eyJhbGciOi..." />
          <label>Куки {cfg.cookies_set && '(сохранены)'} — запасной вариант</label>
          <textarea rows={2} value={cookies} onChange={(e) => setCookies(e.target.value)} />
          <div className={styles.row}>
            <button className={styles.button} onClick={saveCredentials} disabled={busy === 'creds'}>Сохранить</button>
            <button className={styles.button} onClick={relogin} disabled={busy === 'relogin' || !canRelogin}>
              {busy === 'relogin' ? <LoadingOutlined /> : <ReloadOutlined />} Войти / обновить токен
            </button>
          </div>
        </section>

        <section className={styles.card}>
          <h2>Параметры</h2>
          <div className={styles.grid}>
            <div><label>Интервал проверки, сек (60–3600, по умолчанию 300)</label><input type="number" min="60" max="3600" value={interval} onChange={(e) => setIntervalSec(e.target.value)} /></div>
            <div><label>Больница (номер, по умолчанию 14)</label><input type="number" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)} /></div>
            <div><label>Специальность (номер, по умолчанию 18)</label><input type="number" value={specId} onChange={(e) => setSpecId(e.target.value)} /></div>
            <div><label>Пациент (номер, по умолчанию 60459)</label><input type="number" value={patientId} onChange={(e) => setPatientId(e.target.value)} /></div>
            <div><label>Внешний IP-адрес (пусто — определяется автоматически)</label><input value={realAddress} onChange={(e) => setRealAddress(e.target.value)} placeholder="определяется автоматически" /></div>
          </div>
          <button className={styles.button} onClick={saveParams} disabled={busy === 'params'}>Сохранить параметры</button>
        </section>
      </div>
    </div>
  );
};

export default EhealthPage;
